import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight, Receipt, Wallet } from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, LeerZustand } from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../../lib/immobilien/hinweise';
import { useEinheitSeite } from '../EinheitSeitenKontext';

const zahl = (v: unknown): number => { const x = Number(v); return Number.isFinite(x) ? x : 0; };

/**
 * „Mietkonto“: Soll/Ist/Saldo je Mietverhältnis startet mit der Buchhaltung (README §6, Entwurf liegt vor).
 * Bis dahin wie in der bisherigen Ansicht: alle Rechnungen, die dieser Einheit zugeordnet sind.
 */
export function KontoBereich() {
  const { daten } = useEinheitSeite();
  const navigate = useNavigate();
  const [alle, setAlle] = useState(false);
  const liste = daten.rechnungen;
  const offen = liste.filter(r => (r.status ?? '').toLowerCase() !== 'bezahlt');
  const offenSumme = offen.reduce((s, r) => s + Math.abs(zahl(r.brutto)), 0);
  const sichtbar = alle ? liste : liste.slice(0, 8);

  return (
    <>
      <InnenKarte>
        <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}><Wallet weight="fill" size={20} color="var(--acc)" /></span>
          <div className="flex flex-col flex-1 min-w-0" style={{ flexBasis: 240 }}>
            <span style={{ fontSize: 16, fontWeight: 800 }}>Mietkonto je Mietverhältnis</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Saldo, Soll der letzten 12 Monate, Zahlungseingänge und Rückstand eines Vormieters erscheinen hier, sobald die Buchhaltung angebunden ist.</span>
          </div>
          <Chip ton="neutral">{BUCHHALTUNG_FOLGT}</Chip>
        </div>
      </InnenKarte>

      <InnenKarte>
        <KartenTitel icon={Receipt} rechts={liste.length ? `${offen.length} offen · ${eur(offenSumme)}` : undefined}>Rechnungen zur Einheit</KartenTitel>
        {liste.length === 0 ? (
          <LeerZustand icon={Receipt} titel="Keine Rechnungen" text="Dieser Einheit ist noch keine Rechnung zugeordnet." />
        ) : (
          <div className="flex flex-col" style={{ gap: 4 }}>
            {sichtbar.map(r => {
              const bezahlt = (r.status ?? '').toLowerCase() === 'bezahlt';
              const betrag = Math.abs(zahl(r.brutto));
              return (
                <button key={r.id} type="button" onClick={() => navigate(`/admin/rechnungen/${encodeURIComponent(r.id)}`)}
                  className="immo-hover-flaeche flex items-center text-left cursor-pointer flex-wrap"
                  style={{ gap: 12, padding: '10px 10px', borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)', opacity: bezahlt ? 0.65 : 1 }}>
                  <span className="immo-zahl shrink-0" style={{ width: 82, fontSize: 12, color: 'var(--ink3)' }}>{deDatum(r.datum)}</span>
                  <span className="flex flex-col flex-1 min-w-0" style={{ flexBasis: 180 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{r.bezeichnung || r.rechnungsnummer || 'Rechnung'}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[r.unternehmen, r.kategorie].filter(Boolean).join(' · ') || '–'}</span>
                  </span>
                  <Chip groesse="xs" ton={bezahlt ? 'ok' : 'warn'}>{bezahlt ? `Bezahlt${r.bezahlt_timestamp ? ` ${deDatum(r.bezahlt_timestamp)}` : ''}` : 'Offen'}</Chip>
                  <span className="immo-zahl text-right" style={{ minWidth: 100, fontSize: 14, fontWeight: 800, color: bezahlt ? 'var(--ink3)' : r.richtung === 'ausgang' ? 'var(--ok)' : 'var(--bad)' }}>
                    {r.richtung === 'ausgang' ? '+' : '−'}{eur(betrag)}
                  </span>
                  <CaretRight size={14} color="var(--ink3)" />
                </button>
              );
            })}
            {liste.length > 8 && (
              <ImmoButton groesse={36} onClick={() => setAlle(a => !a)} style={{ alignSelf: 'flex-start', marginTop: 6 }}>
                {alle ? 'Weniger anzeigen' : `Alle ${liste.length} Rechnungen`}
              </ImmoButton>
            )}
          </div>
        )}
      </InnenKarte>
    </>
  );
}
